/**
 * FileImportOverlay — Drag-and-drop file import with animated processing flow.
 *
 * Steps: drag-over → uploading → parsing → preview → commit
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileUp, Check, X, Loader2, Layers, ChevronDown } from 'lucide-react';
import { useMapStore } from '../MapContext';
import { useImportFile, useCommitImport, useMapLayers } from '../api';
import type { ImportPreview } from '../types';

/* ------------------------------------------------------------------ */
/*  Marching ants CSS                                                   */
/* ------------------------------------------------------------------ */

const marchingAntsStyle = {
  backgroundImage: `repeating-linear-gradient(
    0deg, #a855f7, #a855f7 8px, transparent 8px, transparent 16px
  ), repeating-linear-gradient(
    90deg, #a855f7, #a855f7 8px, transparent 8px, transparent 16px
  ), repeating-linear-gradient(
    180deg, #a855f7, #a855f7 8px, transparent 8px, transparent 16px
  ), repeating-linear-gradient(
    270deg, #a855f7, #a855f7 8px, transparent 8px, transparent 16px
  )`,
  backgroundSize: '2px 100%, 100% 2px, 2px 100%, 100% 2px',
  backgroundPosition: '0 0, 0 0, 100% 0, 0 100%',
  backgroundRepeat: 'no-repeat',
  animation: 'marchingAnts 1s linear infinite',
};

/* ------------------------------------------------------------------ */
/*  Processing steps indicator                                          */
/* ------------------------------------------------------------------ */

function ProcessingSteps({ step, featureCount }: { step: string; featureCount: number }) {
  const steps = [
    { key: 'uploading', label: 'Uploading file...' },
    { key: 'parsing', label: 'Parsing features...' },
    { key: 'done', label: `Found ${featureCount} features` },
  ];

  const currentIndex = steps.findIndex(s => s.key === step);

  return (
    <div className="space-y-3">
      {steps.map((s, i) => {
        const isActive = s.key === step;
        const isDone = i < currentIndex || step === 'done';
        return (
          <motion.div
            key={s.key}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.15 }}
            className="flex items-center gap-3"
          >
            <div className={`
              w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300
              ${isDone ? 'bg-emerald-500' : isActive ? 'bg-purple-500' : 'bg-gray-200'}
            `}>
              {isDone ? (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 500 }}>
                  <Check className="w-3.5 h-3.5 text-white" />
                </motion.div>
              ) : isActive ? (
                <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
              ) : (
                <div className="w-2 h-2 rounded-full bg-gray-400" />
              )}
            </div>
            <span className={`text-sm ${isDone ? 'text-emerald-700 font-medium' : isActive ? 'text-purple-700 font-medium' : 'text-gray-400'}`}>
              {s.label}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                      */
/* ------------------------------------------------------------------ */

export default function FileImportOverlay() {
  const [isDragOver, setIsDragOver] = useState(false);
  const [showLayerSelect, setShowLayerSelect] = useState(false);
  const [newLayerName, setNewLayerName] = useState('');
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { importPreview, importStep, setImportPreview, setImportStep, clearImport } = useMapStore();
  const importFile = useImportFile();
  const commitImport = useCommitImport();
  const { data: layerData } = useMapLayers({
    enabled: importStep === 'preview' || importStep === 'committing' || showLayerSelect,
  });
  const layers = layerData?.layers ?? [];

  const handleFile = useCallback(async (file: File) => {
    setImportStep('uploading');
    try {
      const result = await importFile.mutateAsync(file);
      setImportStep('parsing');

      // Brief delay for UX
      await new Promise(r => setTimeout(r, 600));

      setImportPreview(result);
      setImportStep('preview');
      setNewLayerName(file.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      console.error('Import failed:', err);
      clearImport();
    }
  }, [importFile, setImportPreview, setImportStep, clearImport]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const geoFile = files.find(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return ['geojson', 'json', 'kml', 'kmz', 'zip', 'shp', 'gpx', 'csv'].includes(ext || '');
    });
    if (geoFile) handleFile(geoFile);
  }, [handleFile]);

  const handleCommit = useCallback(async () => {
    if (!importPreview) return;
    setImportStep('committing');
    try {
      await commitImport.mutateAsync({
        import_id: importPreview.import_id,
        layer_id: selectedLayerId || undefined,
        layer_name: !selectedLayerId ? newLayerName : undefined,
      });
      clearImport();
    } catch (err) {
      console.error('Commit failed:', err);
      setImportStep('preview');
    }
  }, [importPreview, selectedLayerId, newLayerName, commitImport, setImportStep, clearImport]);

  useEffect(() => {
    const handleImportEvent = (event: Event) => {
      const customEvent = event as CustomEvent<File>;
      if (customEvent.detail instanceof File) {
        handleFile(customEvent.detail);
      }
    };

    window.addEventListener('mapper:import-file', handleImportEvent as EventListener);
    return () => {
      window.removeEventListener('mapper:import-file', handleImportEvent as EventListener);
    };
  }, [handleFile]);

  return (
    <>
      {/* Global drag-and-drop zone */}
      <div
        className="absolute inset-0 z-40 pointer-events-none"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ pointerEvents: isDragOver || importStep !== 'idle' ? 'auto' : 'none' }}
      >
        {/* Drag-over overlay */}
        <AnimatePresence>
          {isDragOver && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-purple-900/30 backdrop-blur-sm flex items-center justify-center"
              style={marchingAntsStyle}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl p-10 text-center max-w-sm"
              >
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <FileUp className="w-16 h-16 text-purple-500 mx-auto mb-4" />
                </motion.div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Drop to Import</h3>
                <p className="text-sm text-gray-500">
                  GeoJSON, KML/KMZ, Shapefile (.zip), GPX, or CSV (with coords)
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Processing overlay */}
        <AnimatePresence>
          {(importStep === 'uploading' || importStep === 'parsing') && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm"
              >
                <ProcessingSteps
                  step={importStep}
                  featureCount={importPreview?.feature_count ?? 0}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Preview bottom action bar */}
      <AnimatePresence>
        {importStep === 'preview' && importPreview && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 min-w-[400px] max-w-lg"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Upload className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900">{importPreview.filename}</h4>
                <p className="text-xs text-gray-500">
                  {importPreview.feature_count} features ready to import
                </p>
              </div>
            </div>

            {/* Layer selection */}
            <div className="mb-4">
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">Import to layer:</label>
              <div className="flex gap-2">
                <button
                  onClick={() => { setSelectedLayerId(null); setShowLayerSelect(false); }}
                  className={`flex-1 text-xs py-2 px-3 rounded-md border transition-all ${
                    !selectedLayerId ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  + New Layer
                </button>
                {layers.length > 0 && (
                  <button
                    onClick={() => setShowLayerSelect(!showLayerSelect)}
                    className={`flex-1 text-xs py-2 px-3 rounded-md border transition-all flex items-center justify-between ${
                      selectedLayerId ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="truncate">{selectedLayerId ? layers.find(l => l.id === selectedLayerId)?.name || 'Layer' : 'Existing Layer'}</span>
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </button>
                )}
              </div>

              {/* Layer dropdown */}
              <AnimatePresence>
                {showLayerSelect && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden mt-1"
                  >
                    <div className="border rounded-md bg-white max-h-32 overflow-y-auto">
                      {layers.map(l => (
                        <button
                          key={l.id}
                          onClick={() => { setSelectedLayerId(l.id); setShowLayerSelect(false); }}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"
                        >
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: l.style.color }} />
                          {l.name}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* New layer name */}
              {!selectedLayerId && (
                <motion.input
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  value={newLayerName}
                  onChange={(e) => setNewLayerName(e.target.value)}
                  placeholder="Layer name..."
                  className="w-full mt-2 text-xs px-3 py-2 rounded-md border border-gray-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none"
                />
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handleCommit}
                disabled={!selectedLayerId && !newLayerName.trim()}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-40 transition-all shadow-sm"
              >
                <Upload className="w-4 h-4" />
                Import {importPreview.feature_count} features
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={clearImport}
                className="py-2.5 px-4 text-sm font-medium text-gray-500 rounded-lg hover:bg-gray-100 transition-all"
              >
                Cancel
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden file input for manual trigger */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".geojson,.json,.kml,.kmz,.zip,.shp,.gpx,.csv"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />

      {/* CSS for marching ants */}
      <style>{`
        @keyframes marchingAnts {
          0% { background-position: 0 0, 0 0, 100% 0, 0 100%; }
          100% { background-position: 0 -16px, 16px 0, 100% 16px, -16px 100%; }
        }
      `}</style>
    </>
  );
}
